import React, { useState, useCallback, useMemo } from 'react';
import { HbrIcon } from '@harbor/elements-react-standalone';
import { lock as lockIcon } from '@harbor/elements/icons';

/* ================================================================== */
/*  TYPES                                                              */
/* ================================================================== */
export type RatingLevel = 0 | 1 | 2 | 3;

export interface FanGroup {
  id: string;
  label: string;
  items: string[];
}

export interface FanQuadrant {
  id: string;
  title: string;
  color: string;
  icon: React.ReactNode;
  groups: FanGroup[];
}

export type FanRatings = Record<string, RatingLevel>;

export interface FanChartProps {
  /** Quadrant definitions with groups and items */
  quadrants: FanQuadrant[];
  /** Current ratings keyed by "quadrantId:itemLabel" */
  ratings: FanRatings;
  /** Called when a ring is clicked — key is "quadrantId:itemLabel", level is the new rating (0 to unrate) */
  onRate: (key: string, level?: RatingLevel) => void;
  /** If set, starts drilled into this quadrant */
  defaultQuadrant?: string;
  /** Optional per-quadrant group colors override. Key = quadrant id, value = array of colors per group index */
  groupColors?: Record<string, string[]>;
  /** Optional level descriptions for the detail sidebar (3 levels, each with heading + bullet list) */
  levelDescriptions?: { heading: string; bullets: string[] }[];
}

/* ================================================================== */
/*  CONSTANTS                                                          */
/* ================================================================== */
export const RATING_LABELS: Record<RatingLevel, string> = { 0: 'Unrated', 1: 'Foundation', 2: 'Advanced', 3: 'Expert' };
export const RATING_TAGS: Record<RatingLevel, string> = { 0: '—', 1: 'L100', 2: 'L200', 3: 'L300' };

const LEVELS = [1, 2, 3] as RatingLevel[];
const RING_OPACITIES: Record<number, number> = { 1: 0.2, 2: 0.55, 3: 1.0 };

const DEFAULT_LEVEL_DESCRIPTIONS = [
  {
    heading: 'L100 – Foundation',
    bullets: ['Basic knowledge & awareness', 'Can explain concepts at a high level', 'Familiar with terminology & tools', 'Completed intro-level training'],
  },
  {
    heading: 'L200 – Advanced',
    bullets: ['Working knowledge & hands-on experience', 'Can configure, demo & troubleshoot', 'Understands competitive positioning', 'Leads workshops & design sessions'],
  },
  {
    heading: 'L300 – Expert',
    bullets: ['Deep expertise & mastery', 'Custom demos, POV/POC leadership', 'Multi-architecture solution design', 'Mentors others & drives strategy'],
  },
];

/* ================================================================== */
/*  UTILITIES                                                          */
/* ================================================================== */
export const getFanKey = (qId: string, item: string) => `${qId}:${item}`;

const toRad = (deg: number) => (deg * Math.PI) / 180;

const arcPath = (cx: number, cy: number, innerR: number, outerR: number, startDeg: number, endDeg: number) => {
  const x1 = cx + innerR * Math.cos(toRad(startDeg));
  const y1 = cy - innerR * Math.sin(toRad(startDeg));
  const x2 = cx + outerR * Math.cos(toRad(startDeg));
  const y2 = cy - outerR * Math.sin(toRad(startDeg));
  const x3 = cx + outerR * Math.cos(toRad(endDeg));
  const y3 = cy - outerR * Math.sin(toRad(endDeg));
  const x4 = cx + innerR * Math.cos(toRad(endDeg));
  const y4 = cy - innerR * Math.sin(toRad(endDeg));
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return [
    `M ${x1} ${y1}`, `L ${x2} ${y2}`,
    `A ${outerR} ${outerR} 0 ${large} 0 ${x3} ${y3}`,
    `L ${x4} ${y4}`,
    `A ${innerR} ${innerR} 0 ${large} 1 ${x1} ${y1}`,
    'Z',
  ].join(' ');
};

const splitLabel = (text: string, maxLen: number): string[] => {
  if (text.length <= maxLen) return [text];
  const mid = Math.floor(text.length / 2);
  let bestIdx = -1;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === ' ') {
      if (bestIdx === -1 || Math.abs(i - mid) < Math.abs(bestIdx - mid)) bestIdx = i;
    }
  }
  if (bestIdx === -1) return [text];
  return [text.slice(0, bestIdx), text.slice(bestIdx + 1)];
};

const avgToTag = (avg: number): string => avg >= 2.5 ? 'L300' : avg >= 1.5 ? 'L200' : avg >= 0.5 ? 'L100' : '—';
const avgToLabel = (avg: number): string => avg >= 2.5 ? 'Expert' : avg >= 1.5 ? 'Advanced' : avg >= 0.5 ? 'Foundation' : 'Unrated';

export const getQuadrantRatingStats = (dq: FanQuadrant, ratings: FanRatings) => {
  const allItems = dq.groups.flatMap(g => g.items);
  const totalItems = allItems.length;
  let ratedCount = 0, sum = 0, l100Count = 0, l200Count = 0, l300Count = 0;
  for (const item of allItems) {
    const r = (ratings[getFanKey(dq.id, item)] || 0) as RatingLevel;
    if (r > 0) { ratedCount++; }
    sum += r;
    if (r >= 1) l100Count++;
    if (r >= 2) l200Count++;
    if (r >= 3) l300Count++;
  }
  const avgRating = totalItems > 0 ? sum / totalItems : 0;
  const overallTag = avgToTag(avgRating);
  return { totalItems, ratedCount, avgRating, overallTag, l100Count, l200Count, l300Count };
};

/* ================================================================== */
/*  LAYOUT TYPES (internal geometry helpers)                          */
/* ================================================================== */
interface OverviewItem {
  key: string; item: string; aStart: number; aEnd: number; qId: string; color: string;
}
interface OverviewGroup {
  groupId: string; items: OverviewItem[]; dividerMathAngle: number; showDivider: boolean;
}
interface OverviewQuadrant {
  qId: string; dq: FanQuadrant; qIdx: number; groups: OverviewGroup[];
  divMath: number; bMathStart: number; bMathEnd: number; bandArcId: string; textR: number;
}
interface DetailItem {
  key: string; item: string; aStart: number; aEnd: number; color: string;
}
interface DetailGroup {
  groupId: string; items: DetailItem[]; dividerAngle: number; showDivider: boolean;
}

/* ================================================================== */
/*  COMPONENT                                                          */
/* ================================================================== */
const FanChart: React.FC<FanChartProps> = ({ quadrants, ratings, onRate, defaultQuadrant, groupColors = {}, levelDescriptions }) => {
  const [hoveredRing, setHoveredRing] = useState<string | null>(null);
  const [selectedQ, setSelectedQ] = useState<string | null>(defaultQuadrant || null);
  const [locked, setLocked] = useState(true);
  const [lockAnimating, setLockAnimating] = useState(false);

  const handleLockToggle = useCallback(() => {
    setLockAnimating(true);
    setLocked(prev => !prev);
    setTimeout(() => setLockAnimating(false), 350);
  }, []);

  const totalAllItems = quadrants.reduce((s, d) => s + d.groups.reduce((gs, g) => gs + g.items.length, 0), 0);
  const overallAvg = useMemo(() => {
    const total = quadrants.reduce((s, d) => s + d.groups.reduce((gs, g) => gs + g.items.length, 0), 0);
    if (total === 0) return 0;
    let sum = 0;
    for (const d of quadrants) {
      for (const g of d.groups) {
        for (const item of g.items) {
          sum += ratings[getFanKey(d.id, item)] || 0;
        }
      }
    }
    return sum / total;
  }, [quadrants, ratings]);
  const overallTag = avgToTag(overallAvg);
  const overallLabel = avgToLabel(overallAvg);

  const gColor = useCallback((dq: FanQuadrant, gIdx: number) => groupColors[dq.id]?.[gIdx] || dq.color, [groupColors]);
  const levels = levelDescriptions || DEFAULT_LEVEL_DESCRIPTIONS;

  const selectedDq = useMemo(() => quadrants.find(d => d.id === selectedQ) || null, [quadrants, selectedQ]);
  const labelCache = useMemo(() => {
    if (!selectedDq) return {} as Record<string, string[]>;
    const cache: Record<string, string[]> = {};
    for (const g of selectedDq.groups) {
      for (const item of g.items) cache[item] = splitLabel(item, 14);
    }
    return cache;
  }, [selectedDq]);

  /* ── OVERVIEW (no quadrant selected) ── */
  const renderOverview = () => {
    const SIZE = 640;
    const cxO = SIZE / 2;
    const cyO = SIZE / 2;
    const minR = 70;
    const maxR = 210;
    const ringR = (maxR - minR) / 3;
    const quadGap = 4;
    const groupGap = 2;
    const itemGap = 0.5;
    const available = 360 - quadGap * quadrants.length;
    const bandInner = maxR + 6;
    const bandOuter = maxR + 28;

    // Pre-compute layout (no mutable cursor in JSX)
    let cursor = 0;
    const layout: OverviewQuadrant[] = quadrants.map((dq, qIdx) => {
      const allItems = dq.groups.flatMap(g => g.items);
      const totalItems = allItems.length;
      const qAngle = (totalItems / totalAllItems) * available;
      const qStart = cursor;
      const innerGroupGap = dq.groups.length > 1 ? groupGap : 0;
      const usableAngle = qAngle - innerGroupGap * Math.max(0, dq.groups.length - 1);

      const groups: OverviewGroup[] = dq.groups.map((group, gIdx) => {
        const groupItemAngle = (group.items.length / totalItems) * usableAngle;
        const gapWithin = itemGap * Math.max(0, group.items.length - 1);
        const perItem = (groupItemAngle - gapWithin) / group.items.length;

        const items: OverviewItem[] = group.items.map((item) => {
          const aStart = cursor;
          const aEnd = cursor + perItem;
          cursor = aEnd + itemGap;
          return { key: getFanKey(dq.id, item), item, aStart, aEnd, qId: dq.id, color: gColor(dq, gIdx) };
        });

        const gEnd = cursor - itemGap;
        cursor = gEnd + innerGroupGap;
        const dividerMathAngle = 90 - (gEnd + innerGroupGap / 2);
        return { groupId: group.id, items, dividerMathAngle, showDivider: gIdx < dq.groups.length - 1 };
      });

      const qEnd = cursor - (dq.groups.length > 1 ? groupGap : 0);
      cursor = qEnd + quadGap;
      const divMath = 90 - (qEnd + quadGap / 2);
      const bMathStart = 90 - qEnd;
      const bMathEnd = 90 - qStart;
      const bandArcId = `qband-${dq.id}`;
      const textR = (bandInner + bandOuter) / 2;
      return { qId: dq.id, dq, qIdx, groups, divMath, bMathStart, bMathEnd, bandArcId, textR };
    });

    const bandDefs = layout.map(({ bandArcId, textR, bMathStart, bMathEnd }) => (
      <path key={bandArcId} id={bandArcId}
        d={`M ${cxO + textR * Math.cos(toRad(bMathEnd - 0.5))} ${cyO - textR * Math.sin(toRad(bMathEnd - 0.5))} A ${textR} ${textR} 0 ${(bMathEnd - bMathStart) > 180 ? 1 : 0} 1 ${cxO + textR * Math.cos(toRad(bMathStart + 0.5))} ${cyO - textR * Math.sin(toRad(bMathStart + 0.5))}`}
        fill="none" />
    ));

    return (
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="se__fan-circle-svg" preserveAspectRatio="xMidYMid meet">
        <defs>{bandDefs}</defs>
        {layout.map(({ qId, dq, qIdx, groups, divMath, bMathStart, bMathEnd, bandArcId }) => (
          <g key={qId} style={{ cursor: 'pointer' }} onClick={() => setSelectedQ(qId)}>
            {groups.map(({ groupId, items, dividerMathAngle, showDivider }) => (
              <g key={groupId}>
                {items.map(({ key, item, aStart, aEnd, color }) => {
                  const rating = (ratings[key] || 0) as RatingLevel;
                  const mathStart = 90 - aEnd;
                  const mathEnd = 90 - aStart;
                  return (
                    <g key={key}>
                      {LEVELS.map(lvl => {
                        const innerRR = minR + (lvl - 1) * ringR;
                        const outerRR = minR + lvl * ringR;
                        const isFilled = rating >= lvl;
                        const ringKey = `${key}:${lvl}`;
                        const isHov = hoveredRing === ringKey;
                        return (
                          <path
                            key={ringKey}
                            d={arcPath(cxO, cyO, innerRR, outerRR, mathStart, mathEnd)}
                            fill={isFilled ? color : '#1e3a5f'}
                            fillOpacity={isFilled ? RING_OPACITIES[lvl] : 0.3}
                            stroke="#0d1b2a"
                            strokeWidth={1}
                            className="se__fan-wedge"
                            style={{ filter: isHov ? 'brightness(1.3)' : undefined, cursor: locked ? 'not-allowed' : 'pointer', opacity: locked ? 0.7 : 1 }}
                            onMouseEnter={() => setHoveredRing(ringKey)}
                            onMouseLeave={() => setHoveredRing(null)}
                            onClick={() => !locked && setSelectedQ(qId)}
                          >
                            <title>{item} — {RATING_LABELS[lvl]}{isFilled ? ' ✓' : ''} (click to drill in)</title>
                          </path>
                        );
                      })}
                    </g>
                  );
                })}
                {showDivider && (
                  <line x1={cxO + minR * Math.cos(toRad(dividerMathAngle))} y1={cyO - minR * Math.sin(toRad(dividerMathAngle))}
                    x2={cxO + maxR * Math.cos(toRad(dividerMathAngle))} y2={cyO - maxR * Math.sin(toRad(dividerMathAngle))}
                    stroke="#4a6a8a" strokeWidth={1} />
                )}
              </g>
            ))}
            {qIdx < quadrants.length - 1 && (
              <line x1={cxO + (minR - 4) * Math.cos(toRad(divMath))} y1={cyO - (minR - 4) * Math.sin(toRad(divMath))}
                x2={cxO + (bandOuter + 2) * Math.cos(toRad(divMath))} y2={cyO - (bandOuter + 2) * Math.sin(toRad(divMath))}
                stroke="#2a4a6a" strokeWidth={2} />
            )}
            <path
              d={arcPath(cxO, cyO, bandInner, bandOuter, bMathStart + 0.5, bMathEnd - 0.5)}
              fill={dq.color}
              fillOpacity={0.2}
              stroke={dq.color}
              strokeWidth={1}
              strokeOpacity={0.5}
              className="se__fan-wedge"
            />
            <text fontSize={10} fontWeight={700} fill={dq.color}
              style={{ pointerEvents: 'none', letterSpacing: '0.1em' } as React.CSSProperties}>
              <textPath href={`#${bandArcId}`} startOffset="50%" textAnchor="middle">
                {dq.title.toUpperCase()}
              </textPath>
            </text>
          </g>
        ))}

        <circle cx={cxO} cy={cyO} r={minR - 28} fill="#0d1b2a" stroke="#2a4a6a" strokeWidth={1.5} />
        <text x={cxO} y={cyO - 8} textAnchor="middle" fontSize={16} fontWeight={800} fill="#e0e7ef" style={{ pointerEvents: 'none' }}>
          {overallTag}
        </text>
        <text x={cxO} y={cyO + 10} textAnchor="middle" fontSize={8} fontWeight={600} fill="#8eafc8" style={{ pointerEvents: 'none' }}>
          {overallLabel.toUpperCase()}
        </text>
        {LEVELS.map(lvl => {
          const r = minR + (lvl - 0.5) * ringR;
          return (
            <text key={lvl} x={cxO} y={cyO - r} textAnchor="middle" dominantBaseline="central"
              fontSize={8} fontWeight={700} fill="#8eafc8" style={{ pointerEvents: 'none', letterSpacing: '0.05em' }}>
              {RATING_TAGS[lvl]}
            </text>
          );
        })}
      </svg>
    );
  };

  /* ── DETAIL (quadrant selected → expanded fan + summary ring for others) ── */
  const renderDetail = () => {
    if (!selectedDq) return null;
    const dq = selectedDq;
    const isLight = !!defaultQuadrant;
    const W = 700;
    const H = 540;
    // The fan only starts about 120 units down, so the profile view crops the empty band above it
    // and tucks the domain key into the free top-left corner.
    const TOP_CROP = 90;
    const keyTop = isLight ? TOP_CROP + 14 : 12;
    const cxD = W - 10;
    const cyD = H - 10;
    const minR = 40;
    const maxR = 400;
    const ringR = (maxR - minR) / 3;
    const fanStart = 95;
    const fanEnd = 178;
    const totalAngle = fanEnd - fanStart;
    const allItems = dq.groups.flatMap(g => g.items);
    const totalItems = allItems.length;
    const innerGroupGap = dq.groups.length > 1 ? 2 : 0;
    const itemGap = 0.5;
    const usableAngle = totalAngle - innerGroupGap * Math.max(0, dq.groups.length - 1);

    const bgFill = isLight ? '#ffffff' : '#1e3a5f';
    const strokeColor = isLight ? '#d4dbe4' : '#0d1b2a';
    const labelColor = isLight ? '#1a2332' : '#e0e7ef';
    const lvlLabelColor = isLight ? '#64748b' : '#8eafc8';
    const dividerColor = isLight ? '#94a3b8' : '#4a6a8a';

    // Pre-compute layout (no mutable cursor in JSX)
    let cursor = fanStart;
    const detailLayout: DetailGroup[] = dq.groups.map((group, gIdx) => {
      const groupItemAngle = (group.items.length / totalItems) * usableAngle;
      const gapWithin = itemGap * Math.max(0, group.items.length - 1);
      const perItem = (groupItemAngle - gapWithin) / group.items.length;

      const items: DetailItem[] = group.items.map((item) => {
        const aStart = cursor;
        const aEnd = cursor + perItem;
        cursor = aEnd + itemGap;
        return { key: getFanKey(dq.id, item), item, aStart, aEnd, color: gColor(dq, gIdx) };
      });

      const gEnd = cursor - itemGap;
      cursor = gEnd + innerGroupGap;
      const dividerAngle = gEnd + innerGroupGap / 2;
      return { groupId: group.id, items, dividerAngle, showDivider: gIdx < dq.groups.length - 1 };
    });

    return (
      <>
        <div className={`se__fan-detail-header ${isLight ? 'se__fan-detail-header--light' : ''}`}>
          {!defaultQuadrant && <button className="se__fan-back" onClick={() => setSelectedQ(null)}>← Back</button>}
          <span className="se__fan-icon" style={{ color: dq.color, display: 'inline-flex' }}>{dq.icon}</span>
          <h4 className="se__fan-title" style={{ color: isLight ? '#1a2332' : '#fff' }}>{defaultQuadrant ? 'Competency' : dq.title}</h4>
        </div>

        <div className={`se__fan-detail-layout ${isLight ? 'se__fan-detail-layout--light' : ''}`}>
          {/* Left: level legend */}
          <div className="se__fan-detail-sidebar">
            {levels.map((lvl, i) => (
              <div key={i} className="se__fan-level-section">
                <h5 className="se__fan-level-heading">
                  <span className="se__fan-level-swatch" style={{ background: dq.color, opacity: i === 0 ? 0.15 : i === 1 ? 0.5 : 1 }} />
                  {lvl.heading}
                </h5>
                <ul className="se__fan-level-list">
                  {lvl.bullets.map((b, j) => <li key={j}>{b}</li>)}
                </ul>
              </div>
            ))}
          </div>

          {/* Right: fan chart */}
          <div className="se__fan-detail-chart">
            <svg viewBox={`100 ${isLight ? TOP_CROP : 0} ${W - 100} ${H + 30 - (isLight ? TOP_CROP : 0)}`} className="se__fan-svg" preserveAspectRatio="xMaxYMax meet">
              {detailLayout.map(({ groupId, items, dividerAngle, showDivider }) => (
                <g key={groupId}>
                  {items.map(({ key, item, aStart, aEnd, color }) => {
                    const rating = (ratings[key] || 0) as RatingLevel;
                    const midAngle = (aStart + aEnd) / 2;
                    const lblRD = maxR + 10;
                    const lx = cxD + lblRD * Math.cos(toRad(midAngle));
                    const ly = cyD - lblRD * Math.sin(toRad(midAngle));
                    const textRot = 180 - midAngle;
                    const lines = labelCache[item] || splitLabel(item, 14);
                    return (
                      <g key={key}>
                        {LEVELS.map(lvl => {
                          const innerRR = minR + (lvl - 1) * ringR;
                          const outerRR = minR + lvl * ringR;
                          const isFilled = rating >= lvl;
                          const ringKey = `${key}:${lvl}`;
                          const isHov = hoveredRing === ringKey;
                          return (
                            <path key={ringKey}
                              d={arcPath(cxD, cyD, innerRR, outerRR, aStart, aEnd)}
                              fill={isFilled ? color : bgFill}
                              fillOpacity={isFilled ? RING_OPACITIES[lvl] : 1}
                              stroke={strokeColor} strokeWidth={1}
                              className="se__fan-wedge"
                              style={{ filter: isHov ? 'brightness(1.15)' : undefined, cursor: locked ? 'not-allowed' : 'pointer', opacity: locked ? 0.7 : 1 }}
                              onMouseEnter={() => setHoveredRing(ringKey)}
                              onMouseLeave={() => setHoveredRing(null)}
                              onClick={() => !locked && onRate(key, rating === lvl ? 0 : lvl)}>
                              <title>{item} — {RATING_LABELS[lvl]}{isFilled ? ' ✓' : ''}</title>
                            </path>
                          );
                        })}
                        <text x={lx} y={ly} textAnchor="end" dominantBaseline="central"
                          fontSize={14} fontWeight={600} fill={labelColor}
                          transform={`rotate(${textRot}, ${lx}, ${ly})`}
                          style={{ pointerEvents: 'none' }}>
                          {lines.length === 1 ? lines[0] : (
                            <><tspan x={lx} dy="-0.55em">{lines[0]}</tspan><tspan x={lx} dy="1.1em">{lines[1]}</tspan></>
                          )}
                        </text>
                      </g>
                    );
                  })}
                  {showDivider && (
                    <line x1={cxD + minR * Math.cos(toRad(dividerAngle))} y1={cyD - minR * Math.sin(toRad(dividerAngle))}
                      x2={cxD + (maxR + 4) * Math.cos(toRad(dividerAngle))} y2={cyD - (maxR + 4) * Math.sin(toRad(dividerAngle))}
                      stroke={dividerColor} strokeWidth={1.5} strokeDasharray="4 3" />
                  )}
                </g>
              ))}

              {/* Group key inside chart */}
              {dq.groups.length > 1 && (
                <g>
                  {dq.groups.map((g, i) => (
                    <g key={g.id}>
                      <circle cx={112} cy={keyTop + i * (isLight ? 30 : 26)} r={isLight ? 7 : 6} fill={gColor(dq, i)} />
                      <text x={126} y={keyTop + i * (isLight ? 30 : 26)} dominantBaseline="central" fontSize={isLight ? 17 : 14} fontWeight={700} fill={labelColor}>{g.label}</text>
                    </g>
                  ))}
                </g>
              )}

              {/* Level labels along bottom */}
              {LEVELS.map(lvl => {
                const r = minR + (lvl - 0.5) * ringR;
                const lx = cxD - r;
                const ly = cyD + 18;
                return (
                  <text key={lvl} x={lx} y={ly} textAnchor="middle" dominantBaseline="hanging"
                    fontSize={10} fontWeight={700} fill={lvlLabelColor}
                    style={{ pointerEvents: 'none', letterSpacing: '0.05em' }}>
                    {RATING_TAGS[lvl]}
                  </text>
                );
              })}
            </svg>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="se__design se__design--fan">
      <div style={{ position: 'relative' }}>
        <button
          onClick={handleLockToggle}
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 10,
            width: 34,
            height: 34,
            borderRadius: '50%',
            border: `1.5px solid ${locked ? '#f59e0b' : '#cbd5e1'}`,
            background: locked ? 'rgba(245,158,11,0.12)' : 'rgba(255,255,255,0.88)',
            color: locked ? '#d97706' : '#94a3b8',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: locked ? '0 0 0 3px rgba(245,158,11,0.15)' : '0 1px 4px rgba(0,0,0,0.1)',
            transition: 'background 0.25s ease, border-color 0.25s ease, color 0.25s ease, box-shadow 0.25s ease',
            padding: 0,
          }}
          title={locked ? 'Unlock to edit ratings' : 'Lock to prevent accidental edits'}
        >
          <span style={{
            display: 'inline-flex',
            fontSize: 16,
            transform: lockAnimating
              ? 'scale(0.6) rotate(25deg)'
              : locked ? 'scale(1) rotate(0deg)' : 'scale(1) rotate(-20deg)',
            transition: lockAnimating
              ? 'transform 0.12s ease-in'
              : 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}>
            <HbrIcon asset={lockIcon} />
          </span>
        </button>
      {selectedQ ? renderDetail() : (
        <>
          {renderOverview()}
          <div className="se__fan-legend" style={{ justifyContent: 'center', marginTop: 12 }}>
            {quadrants.map(dq => (
              <div key={dq.id} className="se__fan-legend-item" style={{ cursor: 'pointer' }} onClick={() => setSelectedQ(dq.id)}>
                <span className="se__fan-legend-swatch" style={{ background: dq.color, opacity: 0.9 }} />
                <span className="se__fan-legend-label se__fan-legend-label--dark" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ color: dq.color, display: 'inline-flex' }}>{dq.icon}</span> {dq.title}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
      <p className="se__fan-hint">{selectedQ ? 'Click a ring to set level · Click again to unrate' : 'Click a quadrant to drill in'}</p>
      </div>
    </div>
  );
};

export default FanChart;
